import {
    ResourceListOfOptionBasicDto,
    OptionBasicDto,
    LanguageDto,
    WorkflowDto,
} from "../../services/Client";

import {EditableValue} from "../../components/BaseColumn";
import {getRowStatusProps, getDtoStatus, parseWorkflowString} from "../../services/WorkflowUtils";

import {createMultiLineValues, mergeOptionBasicTexts} from "./BaseOptionsTextUtils";
import IBaseOptionsRow from "./BaseOptionsRow";

/**
 * Create a new row from a given DTO.
 *
 * @param dto the input DTO do convert to a row
 * @param languageList all languages supported by the system, used for OptionBasicTextDtos
 * @param workflowList all workflows known to the system, used for workflow status
 * @returns the created row object
 */
export const createRowFromDto = (dto: OptionBasicDto, languageList: LanguageDto[], workflowList: WorkflowDto[]): IBaseOptionsRow => ({
    category: dto.category,
    categoryText: dto.categoryText,
    optionType: dto.optionType,
    optionCode: dto.optionCode,
    references: {
        dto: dto,
        optionCount: dto.optionCount,
    },
    partNumber: dto.partNumber,
    comment: dto.comment,
    intSequence: dto.intSequence,

    pdipFlag: dto.pdipFlag,
    pdipMFamilie: dto.pdipMFamilie,
    pdipMFamilieBez: dto.pdipMFamilieBez,

    pccdFlag: dto.pccdFlag,

    shorttext: dto.texts ? createMultiLineValues(dto.texts, "shorttext", languageList) : [],
    longtext: dto.texts ? createMultiLineValues(dto.texts, "longtext", languageList) : [],
    shortPCCDtext: dto.optionBasicTextPCCDExports ? createMultiLineValues(dto.optionBasicTextPCCDExports, "shorttext", languageList) : [],
    longPCCDtext:dto.optionBasicTextPCCDExports ? createMultiLineValues(dto.optionBasicTextPCCDExports, "longtext", languageList) : [],

    // get all properties related to the workflow status
    ...getRowStatusProps(dto.status, [], workflowList) as any,

    optionBasicDto: dto, // row data: the source DTO
    languageList: languageList, // row data: all available languages
    workflowList: workflowList, // row data: all available languages

    textExportDate: dto.textExportDate,
    textExportStatus: dto.textExportStatus,

    id: dto.objectId,
    pccdCategoryOverride: dto.pccdCategoryOverride,

    changedBy: dto.metadata ? dto.metadata.changedBy : '',
    changedTimestamp: dto.metadata ? dto.metadata.changedTimestamp : undefined,
    
    pdiptext: dto.pdiptext
    
});

/**
 * Create a new DTO from a given row.
 *
 * @param row the input row to convert to a DTO
 * @returns the created DTO object
 */
export const createDtoFromRow = (row: IBaseOptionsRow): OptionBasicDto => {
    const newDto: OptionBasicDto = Object.assign({}, row.optionBasicDto);
    newDto.category = row.category;
    newDto.optionType = row.optionType;
    newDto.optionCode = row.optionCode;
    newDto.optionCount = row.references && row.references.optionCount;
    newDto.partNumber = row.partNumber;
    newDto.comment = row.comment;
    newDto.status = row.statusValueState !== undefined ? getDtoStatus(row.statusValueState, row.optionBasicDto.status, row.workflowList) : "";
    newDto.texts = mergeOptionBasicTexts(newDto.texts || [], row.shorttext, row.longtext, row.languageList);
    newDto.intSequence = row.intSequence === undefined ? 9999 : row.intSequence;
    newDto.pccdIntSequenceOverride = false;

    return newDto;
};

/**
 * Split a resource list response into option basic / standard equipment
 * and create corresponding rows. For the user the splits are
 * displayed in separate tables but are stored in the same DB.
 *
 * @param res the back end response to be split
 * @param languageList list of all languages known in the system
 * @param workflowList all workflows known to the system
 * @returns an object containing the two splits
 */
export const createRowsFromResourceList = (res: ResourceListOfOptionBasicDto,
                                           languageList: LanguageDto[],
                                           workflowList: WorkflowDto[]
                                        ): IBaseOptionsRow[] => {
    const rows: IBaseOptionsRow[] = [];

    if (res.elements) {
        res.elements.forEach((element: OptionBasicDto) => {
            const info = parseWorkflowString(element.status, workflowList);
            if (info && info.editable !== EditableValue.Unvisible) {
                rows.push(createRowFromDto(element, languageList, workflowList));
            }
        });
    }
    return rows;
};



// WEBPACK FOOTER //
// ./src/views/BaseOptionsView/BaseOptionsRowUtils.ts